import { Link, useNavigate, useLocation } from 'react-router-dom'
import { FaInstagram, FaFacebookF, FaXTwitter, FaYoutube } from 'react-icons/fa6'
import { FiPhone, FiMail, FiArrowUpRight, FiLock, FiMapPin } from 'react-icons/fi'

const Footer = ({ onOpenContact, onOpenDonate }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const footerLinks = [
    { label: 'Home', target: 'hero' },
    { label: 'Welfare Activities', route: '/welfare-activities' },
    { label: 'Awareness & Workshops', route: '/awareness' },
    { label: 'Contact Us', target: 'contact' },
    { label: 'Donate', target: 'donate' },
    { label: 'Online Consultation', target: 'farmvet', url: 'https://farmvet.in' },
  ]

  const handleFooterLinkClick = (e, link) => {
    if (link.url) return;
    e.preventDefault()

    if (link.target === 'donate') {
      if (onOpenDonate) onOpenDonate()
      return
    }

    if (link.target === 'contact') {
      if (onOpenContact) onOpenContact()
      return
    }

    if (link.route) {
      navigate(link.route)
      return
    }

    if (location.pathname !== '/') {
      navigate(`/#${link.target}`)
      return
    }

    const el = document.getElementById(link.target)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <>
      {/* Orange Touchpoint Footer matching mockup */}
      <footer className="bg-brand-orange-500 text-white relative pb-4 sm:pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden text-left animate-fade-in">

        <div className="max-w-8xl mx-auto space-y-6 sm:space-y-16 relative z-10">

          {/* Four Columns Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 sm:gap-10 pt-4 sm:pt-8 border-t border-white/10">

            {/* Col 1: Brand text & socials (5 cols) */}
            <div className="lg:col-span-5 sm:col-span-1 space-y-3 sm:space-y-5">
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="block cursor-pointer focus:outline-none group"
                >
                  <span className="block text-4xl sm:text-5xl tracking-tight text-white leading-none group-hover:text-amber-100 transition-colors" style={{ fontFamily: 'var(--app-font-heading)' }}>CAWT</span>
                  <span className="block text-[8px] uppercase tracking-widest text-white/70 font-bold mt-0.5 sm:mt-1">Animal Welfare Trust</span>
                </Link>
              </div>
              <p className="text-sm xs:text-base sm:text-lg md:text-xl text-slate-600 font-semibold leading-relaxed  text-white/90 max-w-sm">
                Every life matters. Chetak Animal Welfare Trust fights tirelessly for animals through compassion, dignity, love, and hope.
              </p>

              {/* Social icons */}
              <div className="flex items-center gap-2 sm:gap-3">
                {[
                  {
                    name: "instagram",
                    color: "text-[#E1306C]",
                    icon: <FaInstagram className="w-5 h-5" />,
                    url: "#instagram"
                  },
                  {
                    name: "facebook",
                    color: "text-[#1877F2]",
                    icon: <FaFacebookF className="w-4 h-4" />,
                    url: "#facebook"
                  },
                  {
                    name: "twitter",
                    color: "text-slate-900",
                    icon: <FaXTwitter className="w-4 h-4" />,
                    url: "#twitter"
                  },
                  {
                    name: "youtube",
                    color: "text-[#FF0000]",
                    icon: <FaYoutube className="w-5 h-5" />,
                    url: "#youtube"
                  }
                ].map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    className={`w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-white hover:bg-slate-50 ${s.color} flex items-center justify-center shadow-md transition-all hover:scale-110 cursor-pointer`}
                    aria-label={`Follow CAWT on ${s.name}`}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Group wrapper for Quick Links and Contact Info to keep side by side on mobile */}
            <div className="col-span-1 sm:col-span-1 lg:col-span-7 grid grid-cols-2 gap-4 xs:gap-6 sm:gap-8 lg:grid-cols-7 lg:gap-10 pt-1 sm:pt-0">
              {/* Col 2: Quick Links (3 cols) */}
              <div className="lg:col-span-3 space-y-2.5 sm:space-y-4 text-left">
                <h3 className="text-sm xs:text-base sm:text-lg font-black tracking-wider text-white uppercase">
                  Quick Links
                </h3>
                <ul className="space-y-2.5 sm:space-y-3.5 text-sm xs:text-base sm:text-lg md:text-xl text-slate-600 font-semibold leading-relaxed ">
                  {footerLinks.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.url || (link.route ? link.route : `#${link.target}`)}
                        target={link.url ? "_blank" : undefined}
                        rel={link.url ? "noopener noreferrer" : undefined}
                        onClick={(e) => {
                          if (!link.url) handleFooterLinkClick(e, link)
                        }}
                        className="text-white/85 hover:text-white transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Col 3: Contact Info (4 cols) */}
              <div className="lg:col-span-4 space-y-2.5 sm:space-y-4 text-left">
                <h3 className="text-sm xs:text-base sm:text-lg font-black tracking-wider text-white uppercase">
                  Contact Info
                </h3>
                <ul className="space-y-2.5 sm:space-y-3.5 text-sm xs:text-base sm:text-lg md:text-xl text-slate-600 font-semibold leading-relaxed  text-white/95">
                  <li className="flex items-center gap-2 sm:gap-2.5">
                    <FiPhone className="w-4 h-4 text-white shrink-0 mt-0.5" />
                    <a href="tel:+918123038270" className="hover:underline">+91 81230 38270</a>
                  </li>
                  <li className="flex items-center gap-2 sm:gap-2.5">
                    <FiPhone className="w-4 h-4 text-white shrink-0 mt-0.5" />
                    <a href="tel:+919035999372" className="hover:underline">+91 90359 99372</a>
                  </li>
                  <li className="flex items-center gap-2 sm:gap-2.5">
                    <FiMail className="w-4 h-4 text-white shrink-0 mt-0.5" />
                    <a href="mailto:cawtblr@gmail.com" className="hover:underline break-all">cawtblr@gmail.com</a>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-2.5 pt-1">
                    <FiMapPin className="w-4 h-4 text-white shrink-0 mt-1" />
                    <span className="text-white/90 leading-tight">Srinivaspur, Kolar District, Karnataka - 563135</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>

        </div>
      </footer>

      {/* Hearts & Paws Pet Adoption Event Strip */}
      <div className="relative bg-brand-orange-500 overflow-hidden flex flex-col items-center justify-end h-[210px] xs:h-[245px] sm:h-[330px] md:h-[390px] px-4 sm:px-6 select-none">

        {/* Watermark "adopt" with Arrow Action Button right next to 't' */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-20 overflow-hidden"
        >
          <div className="relative inline-flex items-end">
            <span
              className="text-[26vw] xs:text-[28vw] sm:text-[28vw] md:text-[24vw] font-normal text-white/[0.08] tracking-tighter leading-none select-none pointer-events-none -translate-y-1"
              style={{ fontFamily: "'Jumpswinter', sans-serif" }}
            >
              adopt
            </span>
            <div className="pointer-events-auto ml-1 xs:ml-2 sm:ml-3.5 mb-2 xs:mb-3 sm:mb-6 md:mb-9">
              <a
                href="#adoption"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('adoption');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group w-10 h-10 xs:w-11 xs:h-11 sm:w-13 sm:h-13 md:w-14 md:h-14 min-w-[40px] min-h-[40px] bg-white/[0.12] hover:bg-white/[0.25] text-white/80 hover:text-white border border-white/[0.18] rounded-xl sm:rounded-[22px] md:rounded-[24px] shadow-sm flex items-center justify-center transition-all duration-300 hover:scale-105 cursor-pointer backdrop-blur-xs"
                aria-label="Scroll to pet adoption section"
              >
                <FiArrowUpRight className="w-4 h-4 sm:w-6 sm:h-6 text-white/80 group-hover:text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 stroke-[2.5]" />
              </a>
            </div>
          </div>
        </div>

        {/* Peeking Dog/Cat Animation - Noticeably Bigger on Mobile */}
        <div className="absolute bottom-1 xs:bottom-2 sm:bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-10 w-[300px] xs:w-[350px] sm:w-[420px] md:w-[640px] pointer-events-none select-none">
          <video
            src="/cta/adopt.webm"
            width="640"
            height="240"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            className="w-full h-auto block object-contain translate-y-0 pointer-events-none"
          >
            <track kind="captions" src="data:text/vtt,WEBVTT" label="No audio" />
          </video>
        </div>
      </div>

      {/* Bottom Legal & Credits Bar */}
      <div className="bg-brand-orange-600 text-white/95 py-3 sm:py-5 px-4 sm:px-6 lg:px-8 border-t border-white/10 select-none text-[11px] sm:text-xs md:text-sm font-bold relative z-30">
        <div className="max-w-8xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 sm:gap-4 text-center">
          <p className="w-full md:w-auto md:text-left">
            Designed and Developed by{" "}
            <a
              href="https://in.sbdat.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              SB Digital Automation Pvt Ltd
            </a>
          </p>
          <p className="w-full md:w-auto md:text-center">Copyright © {new Date().getFullYear()} CAWT. All Rights Reserved</p>
          <div className="flex flex-nowrap gap-2 sm:gap-3 justify-center w-full md:w-auto md:justify-end items-center whitespace-nowrap text-[11px] sm:text-xs">
            <Link to="/privacy-policy" className="hover:underline text-white/90">Privacy Policy</Link>
            <span className="text-white/40">•</span>
            <Link to="/terms-conditions" className="hover:underline text-white/90">Terms &amp; Conditions</Link>
            <span className="text-white/40">•</span>
            <Link
              to="/admin"
              className="hover:underline text-white/75 hover:text-white transition-colors text-[11px]"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

export default Footer
